import Cookies from "js-cookie"
import { call, put, takeEvery, takeLatest, delay } from "redux-saga/effects"
import * as actionTypes from "./actionTypes"
import * as userActions from "./actions"
import { db } from "helpers/firebase/firebaseConfig"
import { doc, getDoc, getDocs, collection, query, where, updateDoc, addDoc, serverTimestamp } from "firebase/firestore"
import { toast } from "react-toastify"
import { setCookieWithExpiration, removeCookie as removeCookieHelper, getCookieValue } from "helpers/cookieManager"

// Node API configuration
const NODE_API = "https://node-server-eta-coral.vercel.app" // Replace with your actual Node server URL
const NODE_API_KEY = process.env.REACT_APP_NODE_API_KEY

function removeCookie(name) {
  try {
    removeCookieHelper(name)
  } catch (error) {
    console.error("Error removing cookies:", error)
  }
}

// Helper: File -> base64 (no data: prefix)
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => {
    const result = reader.result || ""
    const base64 = String(result).replace(/^data:[^;]+;base64,/, "")
    resolve(base64)
  }
  reader.onerror = reject
  reader.readAsDataURL(file)
})

async function loginUserApi(email, password) {
  try {
    const q = query(collection(db, "service_providers"), where("email", "==", email))
    const querySnapshot = await getDocs(q)
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0]
      const userData = userDoc.data()
      
      if (password === userData.password) {
        await updateDoc(doc(db, "service_providers", userDoc.id), {
          is_online: true
        })

        if (userData.account_status === "paused_for_subscription_expiry") {
          toast.info("إشتراكك منتهي الصلاحية. الرجاء اختيار خطة جديدة أو التواصل مع الدعم لاستعادة الوصول support@dbsaloni.com", { autoClose: false })
          return false
        } else if (userData.account_status === "action_required") {
          toast.info("حسابك معلق. يرجى التواصل مع الدعم لاستعادة الوصول support@dbsaloni.com", { autoClose: false })
          return false
        }
        
        return { ...userData, id: userDoc.id }
      }
    }
    toast.warning("يرجى التحقق من عنوان بريدك الإلكتروني او كلمة المرور ", { autoClose: true })
    return false
  } catch (error) {
    toast.warning("يرجى التحقق من عنوان بريدك الإلكتروني او كلمة المرور ", { autoClose: true })
    return false
  }
}

function* loginUser({ payload: { user, history } }) {
  try {
    const response = yield call(loginUserApi, user.email, user.password)

    if (response) {
      console.log("Login successful, setting up user session...");
      
      // Set cookie first
      setCookieWithExpiration("usersessionId", response.id, 1)
      
      // Dispatch login success action to update Redux state
      yield put(userActions.loginSuccess(response))
      
      // Add delay to ensure Redux state is fully updated and components re-render
      // This prevents the white page issue by letting the DOM update before navigation
      yield delay(500)
      
      console.log("Navigating to dashboard...");
      
      // Then navigate to dashboard
      history("/dashboard")
    } else {
      toast.error("يرجى التحقق من عنوان بريدك الإلكتروني او كلمة المرور ", { autoClose: true })
    }
  } catch (error) {
    console.error("Login error:", error);
    toast.warning("يرجى التحقق من عنوان بريدك الإلكتروني او كلمة المرور ", { autoClose: true })
  }
}

async function getUserProfile(userId) {
  try {
    if (!userId) {
      throw new Error("User ID is undefined or invalid");
    }

    const userDoc = await getDoc(doc(db, "service_providers", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        account_status: userData.account_status,
        bank_account: userData.bank_account,
        account_name: userData.account_name,
        description: userData.description,
        is_online: userData.is_online,
        location: userData.address,
        is_business: userData.is_business,
        profile_img_url: userData.profile_img_url,
        created_at: userData.created_at,
        id: userDoc.id,
        img_id: userData.img_id,
        documentId: userDoc.id,
        full_name: userData.full_name,
        email: userData.email,
        salon_name: userData.salon_name,
        mobile: userData.mobile,
        location_longitude: parseFloat(userData.location_longitude),
        location_latitude: parseFloat(userData.location_latitude),
        closing_time: userData.closing_time,
        opening_time: userData.opening_time,
        tax_number: userData.tax_number || "",
        commission: userData.commission || "",
        commercial_register: userData.commercial_register || "",
        entity_unified_no: userData.entity_unified_no || "",
      }
    }
    return false
  } catch (error) {
    console.error("Error fetching user profile:", error)
    return null
  }
}

async function getRates(userId) {
  try {
    const q = query(collection(db, "customer_ratings"), where("service_provider_id", "==", userId))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    throw error
  }
}

async function getUserInfo(userId) {
  try {
    const userProfile = await getUserProfile(userId)
    return userProfile
  } catch (error) {
    throw error
  }
}

function* fetchUserInfoSaga(action) {
  try {
    const userInfo = yield call(getUserInfo, action.payload)
    const rates = yield call(getRates, action.payload)
    const notifications = yield call(fetchNotificationsApi)
    yield put(userActions.fetchNotificationsSuccess(notifications))

    if (userInfo.account_status === "paused_for_subscription_expiry") {
      toast.info("إشتراكك منتهي الصلاحية. الرجاء اختيار خطة جديدة أو التواصل مع الدعم لاستعادة الوصول support@dbsaloni.com", { autoClose: false })
    } else if (userInfo.account_status === "action_required") {
      toast.info("حسابك معلق. يرجى التواصل مع الدعم لاستعادة الوصول support@dbsaloni.com", { autoClose: false })
      yield put(userActions.logoutUser())
    }

    yield put(userActions.storeUserInfo(userInfo))
    if (rates.length) {
      yield put(userActions.storeUserRate(rates))
    }
  } catch (error) {
    throw error
  }
}

async function updateInfo(id, updatedData) {
  try {
    const updatedFormData = {
      full_name: updatedData.full_name,
      address: updatedData.location,
      bank_account: updatedData.bank_account,
      account_name: updatedData.account_name,
      description: updatedData.description,
      salon_name: updatedData.salon_name,
      mobile: updatedData.mobile,
      closing_time: updatedData.closing_time,
      opening_time: updatedData.opening_time,
      tax_number: updatedData.tax_number,
      commission: updatedData.commission,
      commercial_register: updatedData.commercial_register,
    }

    const userDoc = await getDoc(doc(db, "service_providers", id))
    const oldData = userDoc.data()

    // Handle profile image upload via Node server
    if (updatedData.img) {
      try {
        // Delete old image if exists
        if (oldData.img_id) {
          try {
            await fetch(`${NODE_API}/delete-service-image`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": NODE_API_KEY,
              },
              body: JSON.stringify({ key: oldData.img_id }),
            })
          } catch (delErr) {
            console.warn("Failed to delete old profile image:", delErr)
            // Continue anyway - don't block profile update
          }
        }

        // Upload new image via Node server
        const fileBase64 = await fileToBase64(updatedData.img)
        const uploadRes = await fetch(`${NODE_API}/upload-service-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": NODE_API_KEY,
          },
          body: JSON.stringify({
            fileName: updatedData.img.name,
            contentType: updatedData.img.type || "application/octet-stream",
            fileBase64,
            folder: "providers",
          }),
        })

        if (!uploadRes.ok) {
          const err = await uploadRes.text()
          throw new Error(err || "Profile image upload failed")
        }

        const { url: imgUrl, key: imgKey } = await uploadRes.json()
        updatedFormData.profile_img_url = imgUrl
        updatedFormData.img_id = imgKey
      } catch (uploadErr) {
        console.error("Profile image upload error:", uploadErr)
        toast.error("Failed to upload profile image: " + uploadErr.message)
        throw uploadErr
      }
    }

    await updateDoc(doc(db, "service_providers", id), updatedFormData)

    return id
  } catch (error) {
    console.log("Error updating user info:", error)
    throw error
  }
}

function* updateUser(action) {

  try {
    const documentId = getCookieValue("usersessionId");
    const { ...updatedData } = action.payload

    const updatedInfo = yield call(updateInfo, documentId, updatedData)

    yield put(userActions.fetchUserInfo(updatedInfo))
  } catch (error) {

    throw error
  }
}

async function updateUserLocationService(documentId, locationData) {
  try {
    if (isNaN(locationData.latitude) || isNaN(locationData.longitude)) {
      throw new Error("Invalid location data: must be numbers");
    }

    const userRef = doc(db, 'service_providers', documentId);
    await updateDoc(userRef, {
      location_latitude: locationData.latitude,
      location_longitude: locationData.longitude,
    });
    return documentId;
  } catch (error) {
    console.error("Error updating user location:", error);
    throw error;
  }
}

function* updateUserLocation(action) {
  try {
    const { documentId, locationLatitude, locationLongitude } = action.payload;

    const updatedInfo = yield call(updateUserLocationService, documentId, {
      latitude: parseFloat(locationLatitude),
      longitude: parseFloat(locationLongitude),
    });

    yield put(userActions.fetchUserInfo(updatedInfo));
  } catch (error) {

    yield put(userActions.updateUserLocationFailure(error));
  }
}


async function logout() {
  try {
    const userId = getCookieValue("usersessionId")

    if (!userId) {
      console.warn("No user ID found for logout");
      return;
    }

    const storedToken = localStorage.getItem('firebaseMessagingToken')

    const userDoc = await getDoc(doc(db, "service_providers", userId))
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const existingTokensArray = userData.notification_token || []
      const updatedTokensArray = existingTokensArray.filter(token => token !== storedToken)

      await updateDoc(doc(db, "service_providers", userId), {
        notification_token: updatedTokensArray,
        is_online: false
      })
    }
  } catch (error) {
    console.error("Logout error:", error)
    throw error
  }
}

function* logoutUser(action) {
  const history = action?.payload?.history

  const navigateToLogin = () => {
    if (typeof history === "function") {
      history("/login")
      return
    }

    if (typeof window !== "undefined") {
      window.location.assign("/login")
    }
  }

  try {
    console.log("Logging out user...");
    
    // Clean up Firebase data
    yield call(logout)
    
    // Clear cookies
    removeCookieHelper("usersessionId")
    
    // Clear storage
    localStorage.removeItem('firebaseMessagingToken')
    localStorage.removeItem('firebaseMessagingTokenDispatched')
    
    // Dispatch logout success to clear Redux state
    yield put(userActions.logoutUserSuccess())
    
    // Add delay to ensure Redux state clears and components update
    yield delay(300)
    
    console.log("Navigating to login...");
    
    // Navigate to login
    navigateToLogin()
  } catch (error) {
    console.error("Logout failed:", error);
    // Clear state and redirect anyway to prevent getting stuck
    yield put(userActions.logoutUserSuccess())
    yield delay(300)
    navigateToLogin()
  }
}

async function openClose(status) {
  try {
    const userId = getCookieValue("usersessionId")
    await updateDoc(doc(db, "service_providers", userId), { is_online: status })
    return userId
  } catch (error) {
    throw error
  }
}

function* openShop({ payload: { status } }) {
  try {
    const response = yield call(openClose, status)
    yield put(userActions.fetchUserInfo(response))
  } catch (error) {
    throw error
  }
}

async function changPass(data) {
  try {
    const { newPassword } = data.currentPassword
    const userId = getCookieValue("usersessionId")

    await updateDoc(doc(db, "service_providers", userId), { password: newPassword })
    return userId
  } catch (error) {
    toast.error("  يرجى التحقق من كلمة المرور  ", { autoClose: true })
    throw new Error("  يرجى التحقق من كلمة المرور  ")
  }
}

function* changePassword(action) {
  try {
    const response = yield call(changPass, action.payload)
    yield put(userActions.fetchUserInfo(response))
    yield put({ type: actionTypes.CHANGE_PASSWORD_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: actionTypes.CHANGE_PASSWORD_FAILURE, error })
  }
}


async function updateEmail(newEmail, password) {

  try {
    const userId = getCookieValue("usersessionId")

    // First verify the current password
    const userDoc = await getDoc(doc(db, "service_providers", userId))
    if (!userDoc.exists()) {
      throw new Error("User not found")
    }

    const userData = userDoc.data()

    if (userData.password !== password) {
      throw new Error("Incorrect password")
    }

    // Check if email already exists
    const emailQuery = query(
      collection(db, "service_providers"),
      where("email", "==", newEmail)
    )
    const emailSnapshot = await getDocs(emailQuery)
    if (!emailSnapshot.empty) {
      throw new Error("Email already in use")
    }

    // Update the email
    await updateDoc(doc(db, "service_providers", userId), {
      email: newEmail
    })

    return userId
  } catch (error) {
    console.error("Error changing email:", error)
    throw error
  }
}

function* changeEmail(action) {
  try {
    const { newEmail, password } = action.payload
    const response = yield call(updateEmail, newEmail, password)

    yield put({ type: actionTypes.FETCH_USER_INFO, payload: response })
    yield put({ type: actionTypes.CHANGE_EMAIL_SUCCESS, payload: response })
  } catch (error) {

    yield put({
      type: actionTypes.CHANGE_EMAIL_FAILURE,
    })
  }
}



async function fetchNotificationsApi() {
  const userId = getCookieValue("usersessionId")
  try {
    const q = query(collection(db, "serviceProvidersNotifications"), where("serviceProviders", "==", userId))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    throw error
  }
}

function* fetchNotifications() {
  try {
    const notifications = yield call(fetchNotificationsApi)
    yield put(userActions.fetchNotificationsSuccess(notifications))
  } catch (error) {
    throw error
  }
}

async function clearNotifications(notificationId) {
  try {
    await updateDoc(doc(db, "serviceProvidersNotifications", notificationId), { seen: true })
  } catch (error) {
    throw error
  }
}

function* clearNotificationsSaga({ payload }) {
  try {
    yield call(clearNotifications, payload.notificationId)
    const notifications = yield call(fetchNotificationsApi)
    yield put(userActions.fetchNotificationsSuccess(notifications))
  } catch (error) {
    throw error
  }
}

/**
 * Saves the FCM token to the user's document in Firestore
 */
const saveTokenToFirestore = async (token) => {
  try {
    const userId = Cookies.get("usersessionId", {
      secure: true,
      sameSite: 'None',
      path: "/"
    });

    if (!userId) {
      throw new Error('User ID not found');
    }

    const userRef = doc(db, "service_providers", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const existingTokens = userData.notification_token || [];

      if (!existingTokens.includes(token)) {
        await updateDoc(userRef, {
          notification_token: [...existingTokens, token]
        });
      }
    }
  } catch (error) {
    console.error('Error saving token to Firestore:', error);
    throw error;
  }
};

/**
 * Handles the token saving logic in Redux saga
 */
function* saveUserTokenSaga(action) {
  try {
    const token = action.payload;
    yield call(saveTokenToFirestore, token);
  } catch (error) {
    console.error('Error in saveUserToken saga:', error);
    // Consider adding error handling or retry logic here
  }
}
function* authSaga() {
  yield takeEvery(actionTypes.CLEAR_NOTIFICATIONS, clearNotificationsSaga)
  yield takeEvery(actionTypes.LOGIN_USER, loginUser)
  yield takeEvery(actionTypes.LOGOUT_USER, logoutUser)
  yield takeLatest(actionTypes.FETCH_USER_INFO, fetchUserInfoSaga)
  yield takeLatest(actionTypes.UPDATE_USER_INFO_REQUEST, updateUser)
  yield takeLatest(actionTypes.CHANGE_PASSWORD_REQUEST, changePassword)
  yield takeLatest(actionTypes.CHANGE_EMAIL_REQUEST, changeEmail)
  yield takeLatest(actionTypes.UPDATE_USER_LOCATION_REQUEST, updateUserLocation)
  yield takeLatest(actionTypes.FETCH_NOTIFICATIONS_REQUEST, fetchNotifications)
  yield takeEvery(actionTypes.SAVE_USER_TOKEN, saveUserTokenSaga)
  yield takeEvery(actionTypes.OPEN_SHOP_STATUS, openShop)
}

export default authSaga